/**
 * Admin · Reservas OTA + Material Digital.
 * Registers bookings coming from OTAs (GetYourGuide, Civitatis, Viator…),
 * generates the internal access code and manages download quotas.
 */
import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useMemo, useState } from "react";
import { Link2, Plus, RefreshCw, Search, ShieldOff, ShieldCheck, Sparkles } from "lucide-react";

import {
  listExperienceBookings,
  createOtaBooking,
  getBookingDigitalMaterial,
  manageExperienceDownload,
  type AdminOtaBooking,
  type CreatedOtaBooking,
  type ShareEvent,
} from "@/lib/experience-admin.functions";
import { listUnifiedCatalogLite } from "@/lib/catalog.functions";
import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShareExperience } from "@/components/admin/ShareExperience";
import { BookingNotifications } from "@/components/admin/BookingNotifications";

const OTA_OPTIONS = [
  { value: "getyourguide", label: "GetYourGuide" },
  { value: "civitatis", label: "Civitatis" },
  { value: "viator", label: "Viator / Tripadvisor" },
  { value: "booking", label: "Booking.com" },
  { value: "expedia", label: "Expedia" },
  { value: "airbnb", label: "Airbnb Experiences" },
  { value: "otra", label: "Otra" },
] as const;

const LANGS = ["es", "en", "fr", "pt", "it", "de"] as const;

const catalogQuery = queryOptions({
  queryKey: ["admin", "catalog-lite", "es"],
  queryFn: () => listUnifiedCatalogLite({ data: { lang: "es" } }),
  staleTime: 10 * 60 * 1000,
});

export const Route = createFileRoute("/_authenticated/admin/ota-bookings")({
  head: () => ({
    meta: [
      { title: "Reservas OTA y Material Digital — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: OtaBookingsPage,
});

function OtaBookingsPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-10 sm:px-6 lg:px-8">
        <div>
          <h1 className="font-display text-4xl font-semibold">Reservas OTA</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Registra reservas de canales externos y comparte la Experiencia Digital sin exponer precios.
          </p>
        </div>
        <AdminNav />
        <Suspense fallback={<Skeleton className="h-96 w-full" />}>
          <Body />
        </Suspense>
      </div>
    </div>
  );
}

function Body() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [onlyOta, setOnlyOta] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const listQuery = queryOptions({
    queryKey: ["admin", "experience-bookings", search, onlyOta],
    queryFn: () => listExperienceBookings({ data: { search: search || undefined, onlyOta } }),
  });
  const { data: rows } = useSuspenseQuery(listQuery);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "experience-bookings"] });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por código, referencia OTA, email o tour"
            className="pl-9"
          />
        </div>
        <Button variant={onlyOta ? "default" : "outline"} onClick={() => setOnlyOta((v) => !v)}>
          {onlyOta ? "Solo OTA" : "Todas las reservas"}
        </Button>
        <Button onClick={() => setShowForm((v) => !v)}>
          <Plus className="mr-1.5 h-4 w-4" /> Nueva reserva OTA
        </Button>
      </div>

      {showForm && <OtaForm onDone={invalidate} />}

      <div className="space-y-3">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">Sin resultados.</p>}
        {rows.map((b) => (
          <BookingRow
            key={b.id}
            b={b}
            open={openId === b.id}
            onToggle={() => setOpenId(openId === b.id ? null : b.id)}
          />
        ))}
      </div>
    </div>
  );
}

function OtaForm({ onDone }: { onDone: () => void }) {
  const { data: catalog } = useQuery(catalogQuery);
  const tours = catalog?.tours ?? [];
  const [form, setForm] = useState({
    otaChannel: "getyourguide",
    otaReference: "",
    tourSlug: "",
    travelDate: "",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    travelers: 1,
    lang: "es",
    notes: "",
  });
  const [tourQ, setTourQ] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreatedOtaBooking | null>(null);

  const filtered = useMemo(() => {
    const q = tourQ.trim().toLowerCase();
    if (!q) return tours.slice(0, 8);
    return tours.filter((t) => t.title.toLowerCase().includes(q)).slice(0, 8);
  }, [tourQ, tours]);

  const selected = tours.find((t) => t.slug === form.tourSlug) ?? null;

  const missing = useMemo(() => {
    const m: string[] = [];
    if (!form.otaChannel) m.push("OTA");
    if (!form.tourSlug) m.push("Tour");
    if (!form.travelDate) m.push("Fecha del tour");
    if (!form.customerName.trim()) m.push("Nombre del cliente");
    if (!(Number(form.travelers) >= 1)) m.push("Pasajeros");
    if (!form.lang) m.push("Idioma");
    if (form.customerEmail && !/.+@.+\..+/.test(form.customerEmail)) m.push("Email (formato inválido)");
    return m;
  }, [form]);

  const mutation = useMutation({
    mutationFn: () =>
      createOtaBooking({
        data: {
          otaChannel: form.otaChannel as "getyourguide",
          ...(form.otaReference.trim() ? { otaReference: form.otaReference.trim() } : {}),
          tourSlug: form.tourSlug,
          tourTitle: selected?.title ?? form.tourSlug,
          travelDate: form.travelDate,
          customerName: form.customerName.trim(),
          ...(form.customerEmail.trim() ? { customerEmail: form.customerEmail.trim() } : {}),
          ...(form.customerPhone.trim() ? { customerPhone: form.customerPhone.trim() } : {}),
          travelers: Number(form.travelers) || 1,
          lang: form.lang as "es",
          status: "confirmed",
          ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
        },
      }),
    onSuccess: (b) => {
      setError(null);
      setResult(b);
      if (b.duplicate) toast.warning("Esta reserva OTA ya existe: se muestra la reserva registrada.");
      else {
        const n = b.notifications;
        toast.success(
          `Reserva registrada · ${b.accessCode ?? b.bookingCode}` +
            (n ? ` · Email: ${n.email.status === "sent" ? "enviado" : "no enviado"} · WhatsApp: ${n.whatsapp.status === "sent" ? "enviado" : "no enviado"}` : ""),
        );
      }
      onDone();
    },
    onError: (e: Error) => {
      console.error("[OtaForm] createOtaBooking failed", e);
      setError(e.message || String(e));
    },
  });

  function submit() {
    if (missing.length > 0) {
      setError(`Faltan campos obligatorios: ${missing.join(", ")}.`);
      return;
    }
    setError(null);
    mutation.mutate();
  }

  if (result) {
    return (
      <OtaSuccess
        booking={result}
        otaLabel={OTA_OPTIONS.find((o) => o.value === result.otaChannel)?.label ?? result.otaChannel ?? "—"}
        onAnother={() => {
          setResult(null);
          setForm({ ...form, otaReference: "", customerName: "", customerEmail: "", customerPhone: "", notes: "" });
        }}
      />
    );
  }

  return (
    <div className="space-y-4 rounded-2xl border border-border bg-card p-5">
      <h2 className="font-display text-xl">Registrar reserva OTA</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="text-muted-foreground">Canal (OTA) *</span>
          <select
            value={form.otaChannel}
            onChange={(e) => setForm({ ...form, otaChannel: e.target.value })}
            className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            {OTA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="text-muted-foreground">Código de referencia OTA</span>
          <Input className="mt-1" value={form.otaReference} onChange={(e) => setForm({ ...form, otaReference: e.target.value })} placeholder="GYG-123456" />
        </label>

        <div className="text-sm sm:col-span-2">
          <span className="text-muted-foreground">Tour *</span>
          {selected ? (
            <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
              <span className="truncate font-medium">{selected.title}</span>
              <Button size="sm" variant="ghost" onClick={() => { setForm({ ...form, tourSlug: "" }); setTourQ(""); }}>Cambiar</Button>
            </div>
          ) : (
            <>
              <Input className="mt-1" value={tourQ} onChange={(e) => setTourQ(e.target.value)} placeholder="Buscar tour del catálogo…" />
              <div className="mt-1 max-h-48 space-y-1 overflow-y-auto">
                {filtered.map((t) => (
                  <button
                    key={t.slug}
                    type="button"
                    onClick={() => setForm({ ...form, tourSlug: t.slug })}
                    className="block w-full truncate rounded-md px-3 py-2 text-left text-sm hover:bg-accent"
                  >
                    {t.title}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <label className="text-sm">
          <span className="text-muted-foreground">Fecha del tour *</span>
          <Input className="mt-1" type="date" value={form.travelDate} onChange={(e) => setForm({ ...form, travelDate: e.target.value })} />
        </label>
        <label className="text-sm">
          <span className="text-muted-foreground">Pasajeros *</span>
          <Input className="mt-1" type="number" min={1} value={form.travelers} onChange={(e) => setForm({ ...form, travelers: Number(e.target.value) })} />
        </label>
        <label className="text-sm">
          <span className="text-muted-foreground">Nombre del cliente *</span>
          <Input className="mt-1" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} />
        </label>
        <label className="text-sm">
          <span className="text-muted-foreground">Email (opcional)</span>
          <Input className="mt-1" type="email" value={form.customerEmail} onChange={(e) => setForm({ ...form, customerEmail: e.target.value })} />
        </label>
        <label className="text-sm">
          <span className="text-muted-foreground">Teléfono / WhatsApp (opcional)</span>
          <Input className="mt-1" value={form.customerPhone} onChange={(e) => setForm({ ...form, customerPhone: e.target.value })} placeholder="+52..." />
        </label>
        <label className="text-sm">
          <span className="text-muted-foreground">Idioma de la experiencia *</span>
          <select
            value={form.lang}
            onChange={(e) => setForm({ ...form, lang: e.target.value })}
            className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm uppercase"
          >
            {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          </select>
        </label>
        <label className="text-sm sm:col-span-2">
          <span className="text-muted-foreground">Notas internas</span>
          <Input className="mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </label>
      </div>

      {error && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm">
          <p className="font-semibold text-destructive">No se pudo registrar la reserva.</p>
          <p className="mt-1 whitespace-pre-wrap break-words text-destructive/90">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {missing.length > 0 && (
          <p className="text-xs text-muted-foreground">Faltan: {missing.join(", ")}</p>
        )}
        <Button disabled={mutation.isPending} onClick={submit}>
          {mutation.isPending ? "Registrando…" : "Registrar y generar código"}
        </Button>
      </div>
    </div>
  );
}

function OtaSuccess({
  booking,
  otaLabel,
  onAnother,
}: {
  booking: CreatedOtaBooking;
  otaLabel: string;
  onAnother: () => void;
}) {
  const code = booking.accessCode ?? booking.bookingCode;
  const url = `https://www.mostrandomexico.mx/experiencia/${code}`;

  return (
    <div className="space-y-4 rounded-2xl border border-primary/40 bg-primary/5 p-5">
      <h2 className="font-display text-xl">
        {booking.duplicate ? "Esta reserva OTA ya existe" : "Reserva registrada correctamente"}
      </h2>
      <dl className="grid gap-2 text-sm sm:grid-cols-2">
        <div><dt className="text-muted-foreground">Cliente</dt><dd className="font-medium">{booking.customerName}</dd></div>
        <div><dt className="text-muted-foreground">Tour</dt><dd className="font-medium">{booking.tourTitle}</dd></div>
        <div><dt className="text-muted-foreground">Fecha</dt><dd className="font-medium">{booking.travelDate ?? "—"}</dd></div>
        <div><dt className="text-muted-foreground">Pasajeros</dt><dd className="font-medium">{booking.travelers}</dd></div>
        <div><dt className="text-muted-foreground">OTA</dt><dd className="font-medium">{otaLabel}</dd></div>
        <div><dt className="text-muted-foreground">Idioma</dt><dd className="font-medium">{booking.lang.toUpperCase()}</dd></div>
        <div className="sm:col-span-2">
          <dt className="text-muted-foreground">Código de acceso</dt>
          <dd className="mt-1 inline-block rounded-full bg-accent px-3 py-1 font-semibold text-accent-foreground">{code}</dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-muted-foreground">Experiencia digital</dt>
          <dd className="break-all font-medium">/experiencia/{code}</dd>
        </div>
      </dl>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => { void navigator.clipboard.writeText(code); toast.success("Código copiado"); }}>
          Copiar código
        </Button>
        <Button size="sm" variant="outline" onClick={() => { void navigator.clipboard.writeText(url); toast.success("Enlace copiado"); }}>
          Copiar enlace
        </Button>
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex">
          <Button size="sm" variant="outline"><Link2 className="mr-1.5 h-3.5 w-3.5" /> Abrir experiencia</Button>
        </a>
        <Button size="sm" onClick={onAnother}>Registrar otra</Button>
      </div>

      <BookingNotifications bookingId={booking.id} />
    </div>
  );
}


function BookingRow({ b, open, onToggle }: { b: AdminOtaBooking; open: boolean; onToggle: () => void }) {
  const url = b.accessCode ? `https://www.mostrandomexico.mx/experiencia/${b.accessCode}` : null;

  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-semibold">{b.tourTitle}</p>
          <p className="text-xs text-muted-foreground">
            {b.customerName} · {b.customerEmail} · {b.travelers} pax
            {b.travelDate ? ` · ${b.travelDate}` : ""} · {b.lang.toUpperCase()}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-accent px-2 py-0.5 font-semibold text-accent-foreground">{b.accessCode ?? "—"}</span>
            {b.otaChannel && <span className="rounded-full border border-border px-2 py-0.5">{b.otaChannel}</span>}
            {b.otaReference && <span className="text-muted-foreground">ref. {b.otaReference}</span>}
            <span className="text-muted-foreground">{b.status}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {url && (
            <a href={url} target="_blank" rel="noreferrer" className="inline-flex">
              <Button size="sm" variant="outline"><Link2 className="mr-1.5 h-3.5 w-3.5" /> Vista previa</Button>
            </a>
          )}
          <Button size="sm" onClick={onToggle}>
            <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Material digital
          </Button>
        </div>
      </div>
      {open && <Material booking={b} />}
    </div>
  );
}

function Material({ booking }: { booking: AdminOtaBooking }) {
  const bookingId = booking.id;
  const qc = useQueryClient();
  const [shares, setShares] = useState<ShareEvent[] | null>(null);
  const key = ["admin", "experience-material", bookingId];
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => getBookingDigitalMaterial({ data: { bookingId } }),
  });

  const mutation = useMutation({
    mutationFn: (input: { assetKind: "audio" | "pdf"; assetKey: string; action: "extra" | "reset" | "block" | "unblock"; reason?: string }) =>
      manageExperienceDownload({
        data: {
          bookingId,
          assetKind: input.assetKind,
          assetKey: input.assetKey,
          lang: (data?.lang ?? "es") as "es",
          action: input.action,
          extraAllowed: 1,
          ...(input.reason ? { reason: input.reason } : {}),
        },
      }),
    onSuccess: () => {
      toast.success("Actualizado");
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function act(assetKind: "audio" | "pdf", assetKey: string, action: "extra" | "reset" | "block" | "unblock") {
    const reason = action === "block" || action === "extra"
      ? window.prompt("Motivo (queda registrado en auditoría):") ?? undefined
      : undefined;
    if ((action === "extra" || action === "block") && !reason) return;
    mutation.mutate({ assetKind, assetKey, action, ...(reason ? { reason } : {}) });
  }

  if (isLoading) return <Skeleton className="mt-4 h-40 w-full" />;
  if (!data) return <p className="mt-4 text-sm text-muted-foreground">Esta reserva aún no tiene código de acceso.</p>;

  const assets = [...data.audios, ...(data.pdf ? [data.pdf] : [])];

  return (
    <div className="mt-4 space-y-4 rounded-xl border border-border/60 bg-background/60 p-4">
      {data.experienceUrl && (
        <ShareExperience
          booking={{
            id: booking.id,
            customerName: booking.customerName,
            customerEmail: booking.customerEmail,
            customerPhone: booking.customerPhone,
            tourTitle: booking.tourTitle,
            travelDate: booking.travelDate,
            status: booking.status,
          }}
          url={data.experienceUrl}
          shares={shares ?? data.shares}
          onShares={setShares}
        />
      )}
      <p className="text-xs text-muted-foreground">
        Idioma: {data.lang.toUpperCase()} · Tour: {data.tourSlug ?? "—"}
      </p>
      <div className="space-y-2">
        {assets.length === 0 && <p className="text-sm text-muted-foreground">Sin material digital disponible.</p>}
        {assets.map((a) => (
          <div key={`${a.kind}-${a.key}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 px-3 py-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">
                {a.kind === "pdf" ? "Guía PDF" : a.title}
              </p>
              <p className="text-xs text-muted-foreground">
                {a.used}/{a.allowed} descargas {a.blocked ? "· bloqueado" : ""}
                {a.lastDownloadAt ? ` · última: ${new Date(a.lastDownloadAt).toLocaleString("es-MX")}` : ""}
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Button size="sm" variant="outline" onClick={() => act(a.kind, a.key, "extra")}>
                <Plus className="mr-1 h-3.5 w-3.5" /> Descarga extra
              </Button>
              <Button size="sm" variant="ghost" onClick={() => act(a.kind, a.key, "reset")}>
                <RefreshCw className="mr-1 h-3.5 w-3.5" /> Reiniciar
              </Button>
              {a.blocked ? (
                <Button size="sm" variant="ghost" onClick={() => act(a.kind, a.key, "unblock")}>
                  <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Desbloquear
                </Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => act(a.kind, a.key, "block")}>
                  <ShieldOff className="mr-1 h-3.5 w-3.5" /> Bloquear
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {data.history.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Auditoría</p>
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {data.history.map((h) => (
              <li key={h.id}>
                {new Date(h.createdAt).toLocaleString("es-MX")} · {h.action} · {h.assetKind}/{h.assetKey}
                {h.extraAllowed ? ` (+${h.extraAllowed})` : ""} · {h.grantedByEmail ?? "—"}
                {h.reason ? ` · ${h.reason}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
